import React from 'react';
import {
  Button,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Receipt24Regular, Dismiss20Regular } from '@fluentui/react-icons';

// The billing-address card, extracted from BillingRentalSection so it can be
// reused on its own (e.g. ProjectDetails' read-only "Billing address" block).
// Renders only the card — callers wrap it in their own SectionShell.
interface BillingAddressCardProps {
  savedBillingAddress: string | null;
  onAddBillingAddress?: () => void;
  /** @deprecated No longer rendered — the "Change" affordance was removed.
   *  To change an address, Remove it then Add again. Kept so existing callers
   *  compile; ignored by the card. */
  onChangeBillingAddress?: () => void;
  /** Clears the saved billing address. Returns the card to its empty state. */
  onRemoveBillingAddress?: () => void;
  /** Read-only: render the address (or an empty hint) with no Add / Change /
   *  Remove affordances. Used where the address is owned elsewhere (e.g. a
   *  project's billing address, inherited from its customer). */
  readOnly?: boolean;
  /** Label shown in the empty state. */
  emptyLabel?: string;
  /** Render the inner "Billing address" label above the address. Default
   *  false — every enclosing section already titles the billing address, so
   *  the inner label is a redundant duplicate. Pass true only if a future
   *  caller renders the card with no surrounding title. */
  showLabel?: boolean;
}

const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Shared min-height so this card lines up exactly with the sibling Customer
    // card. Keep in sync with CustomerSection (cardEmpty/cardEmptyStatic too).
    minHeight: '68px',
    boxSizing: 'border-box',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  content: {
    display: 'flex',
    ...shorthands.gap('12px'),
    alignItems: 'center',
    flex: 1,
    // Allow the flex child to shrink below its content width so the address
    // text can truncate instead of pushing the Change/Remove actions out.
    minWidth: 0,
  },
  // Text column (label + address). Needs minWidth:0 + overflow hidden so the
  // ellipsis on the address line takes effect within the flex row.
  textCol: {
    minWidth: 0,
    ...shorthands.overflow('hidden'),
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
  },
  text: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    // Keep the address (including a long region name) on one line, truncating
    // with an ellipsis rather than wrapping the card.
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  cardEmpty: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '68px',
    boxSizing: 'border-box',
    ...shorthands.gap('12px'),
    width: '100%',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  // Read-only empty state: same chrome, no hover/cursor (not a button).
  cardEmptyStatic: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '68px',
    boxSizing: 'border-box',
    ...shorthands.gap('12px'),
    width: '100%',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  emptyIcon: {
    fontSize: '20px',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  emptyLink: {
    fontSize: '14px',
    color: tokens.colorBrandForeground1,
  },
  emptyText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
});

const BillingAddressCardBase: React.FC<BillingAddressCardProps> = ({
  savedBillingAddress,
  onAddBillingAddress,
  onRemoveBillingAddress,
  readOnly = false,
  emptyLabel,
  showLabel = false,
}) => {
  const styles = useStyles();

  if (!savedBillingAddress) {
    if (readOnly) {
      return (
        <div className={styles.cardEmptyStatic}>
          <Receipt24Regular className={styles.emptyIcon} />
          <span className={styles.emptyText}>
            {emptyLabel || 'No billing address on file.'}
          </span>
        </div>
      );
    }
    return (
      <button type="button" className={styles.cardEmpty} onClick={onAddBillingAddress}>
        <Receipt24Regular className={styles.emptyIcon} />
        <span className={styles.emptyLink}>{emptyLabel || 'Add billing address'}</span>
      </button>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.content}>
        <Receipt24Regular
          style={{ fontSize: '20px', color: tokens.colorNeutralForeground3, flexShrink: 0 }}
        />
        <div className={styles.textCol}>
          {showLabel && <div className={styles.label}>Billing address</div>}
          <div className={styles.text} title={savedBillingAddress}>
            {savedBillingAddress}
          </div>
        </div>
      </div>
      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {onRemoveBillingAddress && (
            <Tooltip content="Remove billing address" relationship="label">
              <Button
                appearance="subtle"
                icon={<Dismiss20Regular />}
                onClick={onRemoveBillingAddress}
                aria-label="Remove billing address"
                style={{ minWidth: 'auto' }}
              />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export const BillingAddressCard = React.memo(BillingAddressCardBase);
export default BillingAddressCard;
